<script setup lang="ts">
import getMeta from "@/classes/getMeta";
import PageHeader from "~/components/layout/PageHeader.vue";
import {PARTNERS} from "~/data/partners";

definePageMeta({
  layout: "default",
  name: "buy",
});

useHead(getMeta(
    `XSQUARE`,
    `XSQUARE`,
));

function getOrigin(url: string) {
  if(!url) return;
  const parts = url.match(/[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&=]*)/gi);
  if(!!parts && Array.isArray(parts) && parts?.length >= 1) {
    return parts[0]?.replace("www.", "");
  } else {
    return url;
  }
}
</script>

<template>
  <div class="content-page">
    <page-header
      h1="Purchase"
      :show-more-button="false"
    >
      <template #content>
        <div class="buy-page-title">
          <h2>
            We offer you the opportunity to join the XSQUARE partner program.
          </h2>
          <p>
            The company's products are included in the register of Russian software, are in demand in government and commercial organizations and meet the requirements of import substitution.
            <br>
            If you have any questions, please contact us at
            <a href="mailto:info@xsquare.ru" class="link underline">info@xsquare.ru</a>
          </p>
        </div>
      </template>
    </page-header>

    <div class="container">
      <ul class="partners-list">
        <li
          v-for="(partner, partnerIndex) in PARTNERS"
          :key="`partner_${partnerIndex}`"
        >
          <div class="logo">
            <img v-if="!!partner.logo" :src="partner.logo" :alt="partner.name"/>
            <p v-if="!!partner.logoText" v-html="partner.logoText"></p>
          </div>
          <div class="common-info">
            <div class="name">{{ partner.name }}</div>
            <div class="inn" v-if="!!partner.inn">ИНН: {{ partner.inn }}</div>
            <div class="address" v-if="!!partner.address">{{ partner.address }}</div>
          </div>
          <div class="links">
            <a :href="partner.link" target="_blank" class="link underline" v-if="!!partner.link">{{ getOrigin(partner.link) }}</a>
            <a :href="`mailto:${partner.email}`" class="link underline" v-if="!!partner.email">{{ partner.email }}</a>
          </div>
        </li>
      </ul>
    </div>
  </div>
</template>


<style lang="scss" scoped>
.partners-list {
  display: flex;
  flex-direction: column;
  margin-top: 70px;
  @include onMD {
    margin-top: 50px;
  }
  @include onXS {
    margin-top: 30px;
  }

  li {
    padding: 40px 0;
    border-bottom: 1px solid lighten($GRAY_LINE, 60%);
    display: grid;
    grid-template-columns: 40% 40% 20%;
    @include font-size(14px);
    --min-font-size: 12px;
    @include onMD {
      padding: 30px 0;
    }
    @include onXS {
      grid-template-columns: 100px 1fr .6fr;
      gap: 20px;
      padding: 20px 0;
    }

    &:first-child {
      padding-top: 0;
    }
    &:last-child {
      padding-bottom: 0;
      border-bottom: none;
    }

    .logo {
      display: flex;
      align-items: center;
      gap: 15px;
      @include onSM {
        align-items: center;
        justify-content: center;
        text-align: center;
        flex-direction: column;
      }
      img {
        max-width: 140px;
        max-height: 80px;
        object-fit: contain;
        object-position: left center;
        margin-top: -15px;
        margin-bottom: -15px;
        height: calc(100% + 30px);
        @include onSM {
          width: 120px;
          max-width: 100%;
          max-height: 50px;
          height: auto;
          flex-shrink: 1;
        }
      }
      p {
        @include font-size(14px);
        --min-font-size: 10px;
        padding-top: 10px;
        font-weight: 500;
        color: $RED;
        &:before {
          content: "- ";
          @include onSM {
            display: none;
          }
        }
      }
    }

    .common-info {
      line-height: 1.5em;
      .name {
        font-weight: 600;
      }
      .inn {
        font-weight: 400;
      }
    }

    .links {
      display: flex;
      flex-direction: column;
      align-items: flex-end;
      justify-content: center;
    }
  }
}

:deep(.main-page-header-container) {
  display: grid;
  grid-template-columns: 35% 65%;
  @include onXS {
    display: flex;
    flex-direction: column;
    gap: 20px;
  }
  .buy-page-title {
    color: #FFF;
    @include font-size(24px);
    font-weight: 500;
    line-height: 1.5em;
    letter-spacing: -0.96px;
    p, h2 {
      margin-bottom: 1em;
    }

    @keyframes text-color-anim {
      0% {
        opacity: 1;
      }
      85% {
        opacity: 1;
      }
      90% {
        opacity: .3;
      }
      92% {
        opacity: 1;
      }
      94% {
        opacity: .3;
      }
      96% {
        opacity: 1;
      }
      98% {
        opacity: .3;
      }
      100% {
        opacity: 1;
      }
    }

    .link {
      animation: infinite text-color-anim 3s ease-in-out;
      transition: all .25s ease;
      color: $RED;
      &:hover {
        color: $VIOLET!important;
        animation: none;
      }
    }
  }
}
</style>
